<main class="max-w-4xl mx-auto px-6 pt-8 pb-12 md:pt-12 space-y-12">
  <section class="space-y-4">
    <h1 class="text-2xl font-semibold">Circled Plus — Terms &amp; Conditions</h1>
    <p>These Terms &amp; Conditions (“Terms”) govern your access to and use of the Circled Plus website, Circled Intelligence (CI) and related services (“Platform” and “Services”).</p>
    <p>By accessing, registering on, purchasing a subscription from, or otherwise using the Platform, you agree to these Terms.</p>
    <p>Circled Plus, Circled Intelligence (CI) and Circled Advisory are brands and/or services operated by Circled Proptech LLP (“Circled”, “we”, “us”, “our”), unless expressly stated otherwise.</p>
    <p>Certain real-estate brokerage, transaction facilitation or related regulated services may, where applicable, be provided by Turnip Consultants LLP, a separate legal entity holding the applicable real-estate agent registration. Such services may be subject to separate commercial terms, brokerage arrangements, invoices and statutory requirements.</p>
    <p>Circled Proptech LLP may revise these Terms from time to time. The updated version will be published on the Platform. Your continued use of the Platform after an update constitutes acceptance of the revised Terms.</p>
  </section>

  <section class="space-y-4">
    <h2 class="text-2xl font-semibold">1. Definitions</h2>
    <p>For these Terms:</p>
    <p>“Platform” means the Circled Plus website, application and associated technology, tools and features.</p>
    <p>“Services” include property discovery, curated and private listing access, Circled Intelligence (CI), advisory support, property matching, viewings, introductions and related assistance.</p>
    <p>“Circled Intelligence (CI)” or “CI” means Circled’s proprietary property-intelligence and decision-support offering.</p>
    <p>“User” / “You” means any visitor, registered user, buyer, seller, subscriber or client using the Platform.</p>
    <p>“Listing” means any property details, photographs, specifications, pricing or related information made available through the Platform.</p>
    <p>“Private Listing” means property information made available on a limited-access, confidential or off-market basis.</p>
    <p>“Subscription” / “Paid Plan” means a paid plan that provides access to specified Platform features, property inventory, Circled Intelligence (CI) or other benefits for the applicable subscription period.</p>
    <p>“Transaction Data” means registered sale information, transaction history, pricing information, market information and related property data obtained from available third-party, public, proprietary or market sources.</p>
    <p>“CI Output” includes benchmarks, indicative pricing insights, verdicts, estimated land/UDS information, rental insights, suggested offer references, likely closing ranges and other information generated or displayed by Circled Intelligence (CI).</p>
    <p>“Brokerage Services” means real-estate agency, transaction facilitation or brokerage services provided in connection with a sale or purchase.</p>
    <p>“Confidential Information” means non-public information including private listings, seller details, pricing, negotiations, client information, analytics and transaction-related information.</p>
  </section>

  <section class="space-y-4">
    <h2 class="text-2xl font-semibold">2. Nature and Scope of Circled Plus</h2>
    <p>Circled Plus is a curated real-estate discovery and property-intelligence platform focused on helping users discover and evaluate real-estate opportunities.</p>
    <p>Properties made available through Circled Plus may include:</p>
    <ul class="list-disc pl-6 space-y-2">
      <li>curated market listings;</li>
      <li>private or off-market opportunities;</li>
      <li>seller or owner-direct inventory;</li>
      <li>developer inventory;</li>
      <li>resale opportunities; and</li>
      <li>inventory supplied by authorised intermediaries.</li>
    </ul>
    <p>Availability of any property is subject to change and may depend on eligibility, seller instructions, subscription status and market availability.</p>
    <p>Circled Proptech LLP is not the owner, developer or seller of properties displayed on the Platform unless expressly stated.</p>
    <p>The purchase, sale, booking, conveyance or transfer of a property ultimately takes place between the relevant buyer and seller/developer/owner or authorised representative.</p>
  </section>

  <section class="space-y-4">
    <h2 class="text-2xl font-semibold">3. Brokerage and Turnip Consultants LLP</h2>
    <p>Circled Plus and Circled Proptech LLP provide technology, property discovery, property intelligence and advisory-related services.</p>
    <p>Where a transaction requires real-estate brokerage or regulated transaction facilitation, such services may be provided by Turnip Consultants LLP, subject to its applicable RERA registration and the requirements of applicable law.</p>
    <p>Where Turnip Consultants LLP is involved:</p>
    <ul class="list-disc pl-6 space-y-2">
      <li>it may communicate directly with the buyer, seller, developer or other transaction parties;</li>
      <li>it may issue brokerage terms, engagement documentation or invoices separately;</li>
      <li>brokerage fees may become payable in accordance with the specific transaction or engagement terms communicated to the client; and</li>
      <li>the applicable RERA registration details will be disclosed as required by law.</li>
    </ul>
    <p>A Circled Plus or Circled Intelligence (CI) subscription fee is separate from brokerage unless a written plan, proposal or agreement expressly states otherwise.</p>
    <p>Payment of a Platform subscription does not itself constitute payment of brokerage.</p>
  </section>

  <section class="space-y-4">
    <h2 class="text-2xl font-semibold">4. Circled Intelligence (CI)</h2>
    <p>Circled Intelligence (CI) is an indicative property-intelligence and decision-support tool.</p>
    <p>CI may use, among other inputs:</p>
    <ul class="list-disc pl-6 space-y-2">
      <li>available registered transaction information;</li>
      <li>historical property or project transactions;</li>
      <li>comparable-property or comparable-project information;</li>
      <li>market pricing inputs;</li>
      <li>seller or listing information;</li>
      <li>estimated land-rate information;</li>
      <li>rental market information;</li>
      <li>property specifications;</li>
      <li>third-party information;</li>
      <li>internal research and analysis; and</li>
      <li>proprietary methodologies, assumptions and business logic.</li>
    </ul>
    <p>Depending on the property and availability of information, CI may generate or display insights such as pricing benchmarks, transaction evidence, estimated UDS/land value, rental yield indicators, pricing comparisons, property classifications, suggested offer references and likely closing ranges.</p>
    <h3 class="font-semibold">CI is indicative, not a formal valuation</h3>
    <p>Circled Intelligence (CI) is not:</p>
    <ul class="list-disc pl-6 space-y-2">
      <li>a registered or certified property valuation;</li>
      <li>a legal opinion;</li>
      <li>tax advice;</li>
      <li>investment advice;</li>
      <li>title due diligence;</li>
      <li>a promise of future appreciation;</li>
      <li>a guarantee of rental income;</li>
      <li>a guarantee that a seller will accept a particular offer; or</li>
      <li>a guarantee of the price at which a transaction will conclude.</li>
    </ul>
    <p>CI Outputs are intended to assist users in making more structured and informed property decisions.</p>
  </section>

  <section class="space-y-4">
    <h2 class="text-2xl font-semibold">5. Data Used by Circled Intelligence (CI)</h2>
    <p>Circled uses data from sources reasonably available to it at the time an assessment is generated.</p>
    <p>Such information may originate from third-party databases, public or registered records, owners, sellers, developers, brokers, listing partners, market research, internal records and other sources considered relevant.</p>
    <p>Users acknowledge that:</p>
    <ul class="list-disc pl-6 space-y-2">
      <li>third-party data may contain errors, omissions or inconsistencies;</li>
      <li>registered transaction data may not capture every commercial or property-specific factor;</li>
      <li>data may be delayed or unavailable;</li>
      <li>transaction prices may differ because of floor, view, condition, configuration, documentation, timing, deal structure or other factors;</li>
      <li>comparable projects may not be identical to the subject property;</li>
      <li>land values and rental information may be estimates;</li>
      <li>past transaction history does not guarantee current or future value; and</li>
      <li>a CI Output may change when new information becomes available.</li>
    </ul>
    <p>Circled does not represent that every underlying data point has been independently audited or verified.</p>
    <p>Where information is unavailable, CI may use comparable, estimated or model-derived information and may indicate this within the relevant assessment.</p>
  </section>

  <section class="space-y-4">
    <h2 class="text-2xl font-semibold">6. User Responsibility and Independent Verification</h2>
    <p>Circled Intelligence (CI) and Platform information should form only one part of a user's property evaluation.</p>
    <p>Before entering into any transaction, users should independently verify, as applicable:</p>
    <ul class="list-disc pl-6 space-y-2">
      <li>ownership and title;</li>
      <li>encumbrances;</li>
      <li>litigation;</li>
      <li>RERA registration;</li>
      <li>sanctioned plans and approvals;</li>
      <li>occupancy and completion certificates;</li>
      <li>unit area and measurements;</li>
      <li>UDS or land allocation;</li>
      <li>taxes and statutory dues;</li>
      <li>property condition;</li>
      <li>developer or seller representations;</li>
      <li>pricing;</li>
      <li>financing implications; and</li>
      <li>any other material information relevant to the transaction.</li>
    </ul>
    <p>Users are encouraged to obtain independent legal, financial, tax, technical and other professional advice where appropriate.</p>
  </section>

  <section class="space-y-4">
    <h2 class="text-2xl font-semibold">7. Account Eligibility and Use</h2>
    <p>You must be at least 18 years old and legally capable of entering into a contract to use paid Services.</p>
    <p>You agree to:</p>
    <ul class="list-disc pl-6 space-y-2">
      <li>provide accurate information;</li>
      <li>maintain current contact details;</li>
      <li>protect login credentials;</li>
      <li>not permit unauthorised use of your account; and</li>
      <li>promptly notify Circled if account misuse is suspected.</li>
    </ul>
    <p>You are responsible for activity conducted through your account.</p>
    <p>Circled may approve, reject, limit, suspend or terminate Platform access where reasonably necessary for security, compliance, misuse prevention or protection of confidential inventory.</p>
  </section>

  <section class="space-y-4">
    <h2 class="text-2xl font-semibold">8. Free Access, Subscriptions and Paid Plans</h2>
    <p>Circled Plus may provide limited free access to certain properties, features or Circled Intelligence (CI) previews.</p>
    <p>Additional access may require an approved account and/or a paid Subscription.</p>
    <p>The specific price, subscription period, entitlements, usage limits and benefits of each plan will be communicated on the Platform or in writing at the time of purchase.</p>
    <p>Subscription benefits may include access to additional properties, Circled Intelligence (CI), advanced filters, research, property information or other features.</p>
    <p>Circled may introduce, remove or modify plans and features prospectively.</p>
    <p>Unless expressly stated otherwise, a Subscription does not include:</p>
    <ul class="list-disc pl-6 space-y-2">
      <li>brokerage;</li>
      <li>legal due diligence;</li>
      <li>valuation by a registered valuer;</li>
      <li>tax or financial advice;</li>
      <li>registration/stamp-duty charges;</li>
      <li>loan or financing charges;</li>
      <li>third-party professional fees; or</li>
      <li>government/statutory charges.</li>
    </ul>
  </section>

  <section class="space-y-4">
    <h2 class="text-2xl font-semibold">9. Payments, Activation and Refunds</h2>
    <p>Subscription fees are payable through the payment methods made available on the Platform.</p>
    <p>Access may be activated after successful payment and completion of any applicable account-approval requirements.</p>
    <p>Except where otherwise required by law or expressly stated in writing:</p>
    <p>subscription fees are non-refundable once access or benefits have been activated.</p>
    <p>Payment-processing services may be provided by independent payment partners. Circled does not directly store complete card or banking credentials supplied to such payment processors.</p>
  </section>

  <section class="space-y-4">
    <h2 class="text-2xl font-semibold">10. Private Listings and Confidentiality</h2>
    <p>Private Listings are provided on a restricted and confidential basis.</p>
    <p>By accessing Private Listings, you agree not to:</p>
    <ul class="list-disc pl-6 space-y-2">
      <li>publish or redistribute confidential listing information;</li>
      <li>reproduce private seller information for commercial use;</li>
      <li>upload listings to competing property platforms;</li>
      <li>systematically extract or build a database from Private Listing information;</li>
      <li>circumvent Circled to misuse confidential introductions; or</li>
      <li>use confidential information for purposes unrelated to genuine property evaluation.</li>
    </ul>
    <p>Users should not contact an owner or seller using confidential contact information obtained through Circled except where expressly permitted or facilitated.</p>
    <p>Unauthorised disclosure or misuse may result in immediate suspension or termination of access and may give rise to other remedies available under law or contract.</p>
  </section>

  <section class="space-y-4">
    <h2 class="text-2xl font-semibold">11. Property Availability and Pricing</h2>
    <p>Listings are subject to availability.</p>
    <p>Prices, availability, specifications, possession status and seller expectations may change without notice.</p>
    <p>A property appearing on Circled Plus does not constitute:</p>
    <ul class="list-disc pl-6 space-y-2">
      <li>a binding offer;</li>
      <li>a guarantee that the property remains available;</li>
      <li>a representation that the asking price is fair market value; or</li>
      <li>a guarantee that the property can be purchased at a particular price.</li>
    </ul>
    <p>Private or off-market listings may be withdrawn, sold, reserved or modified by sellers without advance notice.</p>
  </section>

  <section class="space-y-4">
    <h2 class="text-2xl font-semibold">12. User Conduct</h2>
    <p>You must not:</p>
    <ul class="list-disc pl-6 space-y-2">
      <li>impersonate another person;</li>
      <li>deliberately provide materially false information;</li>
      <li>scrape or systematically extract Platform data;</li>
      <li>bypass payment or access restrictions;</li>
      <li>interfere with Platform security;</li>
      <li>reproduce Circled Intelligence (CI) methodologies or datasets for commercial exploitation;</li>
      <li>use Platform information to operate or support a competing database or service;</li>
      <li>engage in unlawful, misleading, defamatory or abusive conduct; or</li>
      <li>use the Platform in violation of applicable law.</li>
    </ul>
  </section>

  <section class="space-y-4">
    <h2 class="text-2xl font-semibold">13. Intellectual Property</h2>
    <p>The Platform and its original content, including branding, design, software, presentation, proprietary datasets, report formats, Circled Intelligence (CI) methodologies, CI Outputs, analysis, curated collections and written materials, are owned by or licensed to Circled Proptech LLP.</p>
    <p>Subscription or Platform access gives the user a limited, personal, non-transferable right to use the relevant content for personal property evaluation.</p>
    <p>It does not transfer ownership of intellectual property.</p>
    <p>Circled Intelligence (CI) reports may not be commercially reproduced, resold, republished or systematically distributed without written permission.</p>
  </section>

  <section class="space-y-4">
    <h2 class="text-2xl font-semibold">14. Communications</h2>
    <p>By providing contact details and using the Services, you agree that Circled may contact you in connection with your enquiry, account, subscription, property preferences or Services through channels including email, telephone and WhatsApp, subject to applicable law and your communication preferences.</p>
    <p>Promotional communications may be opted out of using the options provided.</p>
    <p>Service-related communications necessary to administer your account or transaction may continue where permitted by law.</p>
  </section>

  <section class="space-y-4">
    <h2 class="text-2xl font-semibold">15. Third-Party Services and Links</h2>
    <p>The Platform may rely on or integrate with third-party services including payment gateways, communication platforms, analytics tools, property-information providers, map/location services and other technology providers.</p>
    <p>Circled is not responsible for independent third-party services, availability, policies or acts outside Circled's reasonable control.</p>
  </section>

  <section class="space-y-4">
    <h2 class="text-2xl font-semibold">16. No Guarantee of Transaction Outcome</h2>
    <p>Circled does not guarantee:</p>
    <ul class="list-disc pl-6 space-y-2">
      <li>that a particular property will remain available;</li>
      <li>that a user will successfully purchase a property;</li>
      <li>that a seller will accept a suggested offer;</li>
      <li>that a transaction will close within a CI-generated range;</li>
      <li>that a property will appreciate;</li>
      <li>that a specified rental yield will be achieved; or</li>
      <li>that market conditions will remain unchanged.</li>
    </ul>
    <p>Negotiations and transactions remain subject to the decisions and circumstances of buyers, sellers, developers and other relevant parties.</p>
  </section>

  <section class="space-y-4">
    <h2 class="text-2xl font-semibold">17. Limitation of Liability</h2>
    <p>To the maximum extent permitted by applicable law, Circled Proptech LLP will not be liable for losses arising solely from:</p>
    <ul class="list-disc pl-6 space-y-2">
      <li>inaccuracies or omissions in third-party property or transaction data;</li>
      <li>reliance on indicative CI Outputs without independent verification;</li>
      <li>changes in property pricing or availability;</li>
      <li>negotiations or decisions of buyers, sellers, developers or other transaction parties;</li>
      <li>third-party professional services;</li>
      <li>service outages or failures outside Circled's reasonable control; or</li>
      <li>unauthorised use of a user's account resulting from the user's failure to secure credentials.</li>
    </ul>
    <p>Nothing in these Terms excludes liability that cannot lawfully be excluded.</p>
    <p>Where liability may legally be limited, Circled Proptech LLP's aggregate liability in relation to the relevant paid Platform Service will not exceed the amount paid by the user to Circled Proptech LLP for that particular Service during the applicable subscription period giving rise to the claim.</p>
    <p>Any brokerage engagement with Turnip Consultants LLP will be governed by the applicable brokerage/transaction terms relating to that engagement.</p>
  </section>

  <section class="space-y-4">
    <h2 class="text-2xl font-semibold">18. Indemnity</h2>
    <p>To the extent permitted by law, you agree to indemnify Circled Proptech LLP against reasonable losses, claims or costs arising from:</p>
    <ul class="list-disc pl-6 space-y-2">
      <li>your material breach of these Terms;</li>
      <li>misuse of private or confidential listing information;</li>
      <li>unauthorised commercial use of Platform content;</li>
      <li>unlawful activity carried out through your account; or</li>
      <li>content or information supplied by you that infringes third-party rights.</li>
    </ul>
  </section>

  <section class="space-y-4">
    <h2 class="text-2xl font-semibold">19. Suspension and Termination</h2>
    <p>Circled may restrict, suspend or terminate access where a user:</p>
    <ul class="list-disc pl-6 space-y-2">
      <li>materially breaches these Terms;</li>
      <li>misuses confidential/private inventory;</li>
      <li>attempts to compromise Platform security;</li>
      <li>engages in fraud, abuse or unlawful conduct;</li>
      <li>fails to make required payments; or</li>
      <li>otherwise uses the Platform in a way reasonably likely to cause material harm to Circled, other users or third parties.</li>
    </ul>
    <p>Termination of Platform access does not extinguish payment, confidentiality or other obligations that accrued before termination.</p>
  </section>

  <section class="space-y-4">
    <h2 class="text-2xl font-semibold">20. Governing Law and Jurisdiction</h2>
    <p>These Terms are governed by the laws of India.</p>
    <p>Subject to any mandatory dispute-resolution rights available under applicable law, courts in Bengaluru, Karnataka will have jurisdiction over disputes arising from these Terms.</p>
  </section>

  <section class="space-y-4">
    <h2 class="text-2xl font-semibold">21. Contact</h2>
    <p>Circled Proptech LLP</p>
    <p>Operating Circled Plus, Circled Intelligence (CI) and Circled Advisory</p>
    <p>Bengaluru, Karnataka, India</p>
    <p>Email: <a href="mailto:support&#64;circledplus.co.in" class="underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4">support&#64;circledplus.co.in</a></p>
    <p>For brokerage-related matters involving Turnip Consultants LLP, the applicable entity and registration information will be communicated in the relevant transaction documentation.</p>
  </section>
</main>
